import { useQuery } from "@tanstack/react-query";
import { CoreButton, coreToast } from "@verity/components/core";
import { ProctoringOption } from "@verity/proctoring-option/types";
import { useContext, useState } from "react";
import { renderToStaticMarkup } from "react-dom/server";
import { useNavigate } from "react-router-dom";
import apiClient from "../../../../../helpers/api-client";
import { intIdToUuid } from "../../../../../helpers/uuid";
import i18n from "../../../../../i18n";
import { Institute } from "../../../../../management/store/slices/institutes";
import { PortalControllerContext } from "../../../../context";
import { useSubmitHandlers } from "../hooks/useSubmitHandlers";
import { ConferencePlatformType, InterviewDetailsType } from "../types";

function getInterviewerHtml(interviewersName: string[]) {
	const html = (
		<table>
			{interviewersName.map((interviewer, idx) => (
				<tr key={interviewer}>
					<td style={{ paddingTop: idx === 0 ? "0" : "10px" }}>{interviewer}</td>
				</tr>
			))}
		</table>
	);
	return renderToStaticMarkup(html);
}

type SubmitButtonProps = {
	candidateOptions: Omit<InterviewDetailsType & ConferencePlatformType, "startTime" | "endTime" | "interviewerUuids">;
	proctoringOptions: Partial<ProctoringOption>;
	sessionDates: {
		start: InterviewDetailsType["startTime"];
		end: InterviewDetailsType["endTime"];
	};
	interviewerUuids: InterviewDetailsType["interviewerUuids"];
};

type InterviewerType = Awaited<ReturnType<typeof apiClient.liveClient.getCourseLiveProctorInfo>>[number];

export default function SubmitButton({
	candidateOptions,
	proctoringOptions,
	sessionDates,
	interviewerUuids,
}: SubmitButtonProps) {
	const { portalContext } = useContext(PortalControllerContext);
	const { lmsProvidedCourseId, instituteId } = portalContext;

	const [isLoading, setIsLoading] = useState(false);

	const navigate = useNavigate();

	const {
		data: interviewers,
		isLoading: isInterviewersLoading,
		error: interviewersError,
	} = useQuery<InterviewerType[]>({
		queryKey: ["interviewers", lmsProvidedCourseId],
		queryFn: () => apiClient.liveClient.getCourseLiveProctorInfo(lmsProvidedCourseId!),
		enabled: !!lmsProvidedCourseId,
	});

	const {
		data: companyName,
		isLoading: companyNameLoading,
		error: companyNameError,
	} = useQuery<string>({
		queryKey: ["companyName", instituteId],
		enabled: !!instituteId,
		queryFn: async () => {
			const response = await fetch(`/api/verity/institute/${instituteId}`);

			if (!response.ok) {
				throw new Error("Error getting insititute");
			}

			const data = (await response.json()) as Institute;
			return data.name;
		},
	});

	const {
		handleValidateDates,
		handleSaveProctoringConfig,
		handleSaveLiveSession,
		handleSaveAccessControlUser,
		handleRegisterUsers,
	} = useSubmitHandlers({
		candidateOptions,
		proctoringOptions,
		sessionDates,
		interviewerUuids,
	});

	async function onSubmit() {
		try {
			handleValidateDates();
		} catch (err) {
			console.error(err);
			if (err instanceof Error) {
				coreToast.error(err.message);
			}
			return;
		}

		setIsLoading(true);

		let assessmentId: number | undefined = undefined;
		let assessmentUuid = crypto.randomUUID().replaceAll("-", "");

		try {
			if (!companyName) throw new Error("Cannot get Company Name");

			const candidateId = await handleSaveProctoringConfig(assessmentUuid);

			//If we just saved a new assessment (with a made up id) get the id generated by the db
			if (!assessmentId) {
				const assessment = await apiClient.assessment.getAssessmentByAssessmentInfo({
					courseId: lmsProvidedCourseId,
					quizId: assessmentUuid,
				});
				if (assessment) {
					assessmentId = assessment.id;
					assessmentUuid = assessment.quizId;
				}
			}

			const [sessionId, candidateUuid] = await Promise.all([
				handleSaveLiveSession(intIdToUuid(assessmentId!)),
				handleSaveAccessControlUser(assessmentId, candidateId!, {
					companyName,
					interviewers: getInterviewerHtml(
						interviewerUuids.map((uuid) => {
							return interviewers?.find((i) => i.uuid === uuid)?.name || "Interviewer";
						}),
					),
				}),
			]);

			await handleRegisterUsers(sessionId, candidateUuid);

			setIsLoading(false);
			navigate("/interviews/schedule/submitted", {
				state: {
					candidateName: candidateOptions.candidateName,
					positionName: candidateOptions.positionName,
					date: sessionDates.start,
				},
			});
		} catch (e) {
			setIsLoading(false);
			console.error(e);
			coreToast.error(i18n.t("candidate.scheduleInterview.toast.saveFail"));
		}
	}

	if (interviewersError || companyNameError) throw new Error(interviewersError?.message || companyNameError?.message);

	if (isInterviewersLoading || companyNameLoading)
		return (
			<CoreButton loading disabled>
				{i18n.t(`candidate.scheduleInterview.navigation.schedule`)}
			</CoreButton>
		);
	else
		return (
			<CoreButton onClick={onSubmit} loading={isLoading}>
				{i18n.t(`candidate.scheduleInterview.navigation.schedule`)}
			</CoreButton>
		);
}
